import React, { useState } from 'react';

// ✅ Cleaned up: No external navigation links, no Game banners.
// This component now relies on the Global Layout (App.js) for navigation 
// or stands alone without duplicating the top bar.

const MedicalScenario = () => {
    // --- 1. State Management ---
    const [answers, setAnswers] = useState({});
    const [feedback, setFeedback] = useState({});

    // --- 2. Scenario Data ---
    const scenarios = [
        // Part 1: Patient Communication
        { id: 'q1', category: 'Patient Communication', title: 'A patient is frightened about their diagnosis. How do you explain it with compassion?', prompt: 'Think about: How do you balance honesty and hope?' },
        { id: 'q2', category: 'Patient Communication', title: 'A family member demands full disclosure, but the patient doesn’t want to know. What do you do?', prompt: 'Think about: Patient autonomy vs. family concern.' },
        { id: 'q3', category: 'Patient Communication', title: 'A patient asks, "Am I going to die?" How do you respond?', prompt: 'Think about: Honesty, empathy, and realistic hope.' },
        { id: 'q4', category: 'Patient Communication', title: 'A non-English-speaking patient is in pain. How do you communicate effectively?', prompt: 'Think about: Tools and gestures to bridge the language gap.' },
        { id: 'q5', category: 'Patient Communication', title: 'A parent refuses vaccination for their child. How do you handle the conversation?', prompt: 'Think about: Respect, education, and public health responsibility.' },
        
        // Part 2: Emergency Response
        { id: 'q6', category: 'Emergency Response', title: 'You receive a report of smoke on the 8th floor. What are your immediate actions?', prompt: 'Think about: Who do you inform? Do you evacuate?' },
        { id: 'q7', category: 'Emergency Response', title: 'A guest collapses in the lobby. How do you respond?', prompt: 'Think about: What is your first call? How do you speak to other guests?' },
        { id: 'q8', category: 'Emergency Response', title: 'A fire alarm goes off at 3 AM. What is your procedure?', prompt: 'Think about: How do you verify? When do you announce evacuation?' },
        { id: 'q9', category: 'Emergency Response', title: 'A patient in severe pain demands opioids. You suspect addiction. How do you respond?', prompt: 'Think about: How do you show empathy while maintaining safety?' },
        { id: 'q10', category: 'Emergency Response', title: 'A mass casualty incident occurs. How do you lead triage?', prompt: 'Think about: Prioritization, communication, and calm authority.' },
        { id: 'q11', category: 'Emergency Response', title: 'A patient codes during handover. What do you do?', prompt: 'Think about: Immediate action and team coordination.' },
        { id: 'q12', category: 'Emergency Response', title: 'An ER is overwhelmed with flu cases. How do you manage capacity?', prompt: 'Think about: Triage, staffing, and patient flow.' },

        // Part 3: Ethics & Leadership
        { id: 'q13', category: 'Ethics & Leadership', title: 'A colleague makes a medication error. No harm done. Do you report it?', prompt: 'Think about: Safety culture vs. loyalty.' },
        { id: 'q14', category: 'Ethics & Leadership', title: 'A patient with no ID or insurance needs emergency surgery. What do you do?', prompt: 'Think about: Ethical duty vs. hospital policy.' },
        { id: 'q15', category: 'Ethics & Leadership', title: 'A junior nurse is being bullied by a senior staff member. How do you intervene?', prompt: 'Think about: Creating a safe work environment.' },
        { id: 'q16', category: 'Ethics & Leadership', title: 'You\'re short-staffed and a patient falls. How do you respond?', prompt: 'Think about: Accountability, investigation, and prevention.' },
        { id: 'q17', category: 'Ethics & Leadership', title: 'A patient\'s advance directive says "no resuscitation," but the family demands CPR. What do you do?', prompt: 'Think about: Legal document vs. emotional family request.' },
        { id: 'q18', category: 'Ethics & Leadership', title: 'You witness a doctor making inappropriate comments to a patient. How do you act?', prompt: 'Think about: Professional boundaries and reporting.' },
        { id: 'q19', category: 'Ethics & Leadership', title: 'A patient insists on leaving against medical advice (AMA). What is your procedure?', prompt: 'Think about: Informed consent and documentation.' },
        { id: 'q20', category: 'Ethics & Leadership', title: 'How do you conduct a shift handover to ensure patient safety?', prompt: 'Think about: Clarity, structure, and accountability.' },
    ];

    // --- 3. Logic Functions ---
    const handleInputChange = (id, value) => {
        setAnswers(prev => ({ ...prev, [id]: value }));
    };

    const submitAnswer = (id) => {
        const value = answers[id] || '';
        let feedbackText = '';
        
        if (value.trim().length < 20) {
            feedbackText = "<strong>💡 Tip:</strong> Be specific. Include empathy, clarity, and medical ethics in your response.";
        } else {
            feedbackText = "<strong>✅ Well done!</strong> Your response shows leadership under pressure. Compare your answer with protocol.";
        }
        
        setFeedback(prev => ({ ...prev, [id]: feedbackText }));
    };

    // --- 4. Styling (Mahogany Theme) ---
    const styles = {
        body: { 
            fontFamily: "'Segoe UI', sans-serif", 
            background: '#f7f9fc', 
            color: '#2c3e50', 
            lineHeight: 1.6, 
            minHeight: '100vh',
            position: 'relative'
        },
        header: { 
            textAlign: 'center', 
            padding: '60px 20px 60px', 
            background: '#800020', // Mahogany Red
            color: 'white', 
            borderBottom: '6px solid #b3002d' 
        },
        headerH1: { 
            fontSize: '32px', 
            marginBottom: '10px', 
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)' 
        },
        container: { 
            maxWidth: '900px', 
            margin: '-40px auto 40px', // Overlap header slightly
            padding: '40px', 
            background: 'white', 
            borderRadius: '12px', 
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            position: 'relative',
            zIndex: 2
        },
        sectionTitle: { 
            color: '#800020', 
            marginTop: '40px', 
            marginBottom: '20px', 
            borderBottom: '2px solid #eee', 
            paddingBottom: '10px' 
        },
        exercise: { 
            background: '#fff5f7', // Very light red tint
            padding: '25px', 
            marginBottom: '25px', 
            borderLeft: '5px solid #800020', 
            borderRadius: '8px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
        },
        textarea: { 
            width: '100%', 
            padding: '12px', 
            margin: '15px 0', 
            border: '1px solid #ddd', 
            borderRadius: '6px', 
            fontSize: '16px', 
            minHeight: '80px', 
            fontFamily: 'inherit' 
        },
        btn: { 
            padding: '10px 25px', 
            background: '#800020', 
            color: 'white', 
            border: 'none', 
            borderRadius: '6px', 
            cursor: 'pointer', 
            fontSize: '15px', 
            fontWeight: 'bold',
            transition: 'background 0.3s'
        },
        feedback: { 
            marginTop: '15px', 
            padding: '15px', 
            background: '#e8f4f8', 
            borderRadius: '6px', 
            fontSize: '0.95rem' 
        },
        footer: {
            textAlign: 'center',
            padding: '40px',
            background: '#2c3e50',
            color: '#bdc3c7',
            marginTop: '60px'
        }
    };

    return (
        <div style={styles.body}>
            {/* Header */}
            <header style={styles.header}>
                <h1 style={styles.headerH1}>🏥 Medical Professional Scenarios</h1>
                <p>Solve real operational challenges using English as your tool.</p>
            </header>

            {/* Main Content Container */}
            <div style={styles.container}>
                <h2>🎯 Learning Objectives</h2>
                <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>Lead during triage and mass casualty events.</li>
                    <li>Communicate diagnoses clearly to patients and families.</li>
                    <li>Handle ethical dilemmas with integrity.</li>
                    <li>Coordinate with medical teams under pressure.</li>
                </ul>

                {/* Scenarios Loop */}
                {['Patient Communication', 'Emergency Response', 'Ethics & Leadership'].map(category => (
                    <div key={category}>
                        <h2 style={styles.sectionTitle}>{category}</h2>
                        {scenarios.filter(s => s.category === category).map(scenario => (
                            <div key={scenario.id} style={styles.exercise}>
                                <h3 style={{ color: '#800020', marginBottom: '10px' }}>{scenario.title}</h3>
                                <p><strong>{scenario.prompt}</strong></p>
                                <textarea
                                    style={styles.textarea}
                                    placeholder="Your response..."
                                    value={answers[scenario.id] || ''}
                                    onChange={(e) => handleInputChange(scenario.id, e.target.value)}
                                />
                                <button 
                                    style={styles.btn}
                                    onMouseEnter={(e) => e.target.style.background = '#600018'}
                                    onMouseLeave={(e) => e.target.style.background = '#800020'}
                                    onClick={() => submitAnswer(scenario.id)}
                                >
                                    ✅ Submit
                                </button>
                                {feedback[scenario.id] && (
                                    <div 
                                        style={styles.feedback} 
                                        dangerouslySetInnerHTML={{ __html: feedback[scenario.id] }} 
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* Footer */}
            
        </div>
    );
};

export default MedicalScenario;